import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { updateOrgSettings } from '../orgs/api';
import { SigningModeBadge } from './SigningModeBadge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';

export function SettingsPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const [name, setName] = useState(org?.name ?? '');
  const [logoUrl, setLogoUrl] = useState(org?.logo_url ?? '');
  const [brandColor, setBrandColor] = useState(org?.brand_color ?? '');
  const [supportEmail, setSupportEmail] = useState(org?.support_email ?? '');
  const [allowedOrigins, setAllowedOrigins] = useState((org?.allowed_origins ?? []).join('\n'));

  const save = useMutation({
    mutationFn: () =>
      updateOrgSettings(orgId, {
        name: name.trim(),
        logo_url: logoUrl.trim(),
        brand_color: brandColor.trim(),
        support_email: supportEmail.trim(),
        allowed_origins: allowedOrigins
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (!org) return null;

  const validBrandColor = brandColor === '' || /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(brandColor.trim());

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Settings</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Company name, branding the iframe shows your end users, and where the wallet is allowed to embed.
        </p>
      </div>

      <Card>
        <CardHeader
          title="Signing mode"
          description="The key management model chosen when the company was created. This cannot be changed."
        />
        <CardBody>
          <SigningModeBadge mode={org.signing_mode} />
        </CardBody>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader title="Company" />
          <CardBody>
            <div className="grid gap-5 sm:grid-cols-[1fr_180px]">
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <Input label="Slug" value={org.slug} disabled hint="Slug is permanent." />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Branding"
            description="Surfaced in the wallet iframe and recovery emails so end users recognise it as yours."
          />
          <CardBody>
            <div className="space-y-5">
              <Input
                label="Logo URL"
                placeholder="https://yourapp.com/logo.svg"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                hint="Square PNG/SVG. Used in the iframe header."
              />
              <div className="grid gap-5 sm:grid-cols-[1fr_120px] sm:items-end">
                <Input
                  label="Brand color"
                  placeholder="#1E50E0"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  hint="Hex like #RRGGBB. Used as the iframe accent."
                />
                <div
                  className="h-10 rounded-md border border-line"
                  style={{ background: validBrandColor && brandColor ? brandColor : 'transparent' }}
                  aria-hidden
                />
              </div>
              {!validBrandColor && (
                <p className="text-sm text-bad">Brand color must be #RRGGBB or #RRGGBBAA.</p>
              )}
              <Input
                label="Support email"
                type="email"
                placeholder="support@yourapp.com"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                hint="Where end users contact you. Included in recovery emails."
              />
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Allowed origins"
            description="Origins where the wallet iframe is allowed to embed. The iframe rejects postMessages from anywhere else."
          />
          <CardBody>
            <textarea
              value={allowedOrigins}
              onChange={(e) => setAllowedOrigins(e.target.value)}
              rows={4}
              className="block w-full rounded-md border border-line bg-white px-3 py-2 font-mono text-sm text-ink-primary placeholder:text-ink-muted focus:border-ink-primary focus:outline-none focus:ring-1 focus:ring-ink-primary"
              placeholder={'https://app.example.com\nhttps://staging.example.com'}
            />
            <p className="mt-2 text-xs text-ink-tertiary">One origin per line.</p>
          </CardBody>
        </Card>

        <div className="flex items-center justify-end gap-4">
          {save.isError && (
            <p className="text-sm text-bad">
              {save.error instanceof Error ? save.error.message : 'Could not save'}
            </p>
          )}
          {save.isSuccess && <p className="text-sm text-ok">Saved.</p>}
          <Button type="submit" disabled={save.isPending || !validBrandColor}>
            {save.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
